import React, { useEffect, useRef } from 'react';

const NeuralNetworkBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const ctx = canvas.getContext('2d', { alpha: true });
        if (!ctx) return;

        let animationFrameId: number;
        let particles: any[] = [];
        let time = 0;
        let blasted = false;

        const getParams = () => {
            const width = window.innerWidth;
            const isMobile = width < 768;
            const isTablet = width < 1200;

            return {
                count: isMobile ? 30 : isTablet ? 50 : 70, // reduced for calmer first load
                connectionDist: isMobile ? 100 : 140,
                baseOpacity: 0.35,
                lineOpacity: 0.45,
                particleSize: isMobile ? 0.8 : 1.2,
            };
        };

        const init = () => {
            const rect = container.getBoundingClientRect();
            const width = rect.width;
            const height = rect.height;
            const dpr = Math.min(window.devicePixelRatio || 1, 2);

            canvas.width = width * dpr;
            canvas.height = height * dpr;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.scale(dpr, dpr);

            const params = getParams();
            particles = [];

            for (let i = 0; i < params.count; i++) {
                const x = Math.random() * width;
                const y = Math.random() * height;
                const z = Math.random() * 2 + 0.5;

                particles.push({
                    x, y, z,
                    originX: x,
                    originY: y,
                    vx: (Math.random() - 0.5) * 0.15,
                    vy: (Math.random() - 0.5) * 0.15,
                    size: (Math.random() * 1.5 + 1.2) * params.particleSize,
                    phase: Math.random() * Math.PI * 2,
                    speed: 0.008 + Math.random() * 0.015,
                    color: i % 3 === 0 ? '80, 160, 255' : i % 3 === 1 ? '100, 180, 255' : '60, 140, 240',
                    glow: 0,
                    pulseSpeed: 0.5 + Math.random() * 1.5
                });
            }
        };

        const triggerBlast = () => {
            const rect = container.getBoundingClientRect();
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;

            particles.forEach(p => {
                const dx = p.x - centerX;
                const dy = p.y - centerY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const angle = Math.atan2(dy, dx);

                // Reduced force: was 25–40, now 6–12
                const shockMultiplier = Math.max(0.1, (800 - dist) / 800);
                const force = (6 + Math.random() * 6) * shockMultiplier / p.z;

                p.vx = Math.cos(angle) * force;
                p.vy = Math.sin(angle) * force;
                p.glow = 0.4; // reduced from 1.0 — less initial flash
            });
            blasted = true;
            window.dispatchEvent(new CustomEvent('nn-blasted'));
        };

        let isVisible = true;
        const observer = new IntersectionObserver(
            ([entry]) => {
                isVisible = entry.isIntersecting;
                if (isVisible) {
                    draw();
                }
            },
            { threshold: 0.1 }
        );

        if (container) observer.observe(container);

        const draw = () => {
            if (!isVisible) {
                cancelAnimationFrame(animationFrameId);
                return;
            }

            const rect = container.getBoundingClientRect();
            const width = rect.width;
            const height = rect.height;
            const params = getParams();

            ctx.clearRect(0, 0, width, height);
            time += 0.01;

            ctx.globalCompositeOperation = 'lighter';

            // Draw connections
            particles.forEach((p, i) => {
                const pMaxDist = params.connectionDist * (1 / p.z);
                let connections = 0;
                const maxConnections = 3;

                for (let j = i + 1; j < particles.length && connections < maxConnections; j++) {
                    const p2 = particles[j];
                    const dx = p.x - p2.x;
                    const dy = p.y - p2.y;

                    if (Math.abs(dx) > params.connectionDist || Math.abs(dy) > params.connectionDist) continue;

                    const distSq = dx * dx + dy * dy;
                    const maxDist = (pMaxDist + params.connectionDist * (1 / p2.z)) / 2;

                    if (distSq < maxDist * maxDist) {
                        const dist = Math.sqrt(distSq);
                        const opacity = (1 - dist / maxDist) * params.lineOpacity * (1.1 + p.glow + p2.glow);

                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.strokeStyle = `rgba(${p.color}, ${opacity})`;
                        ctx.lineWidth = 0.5 / ((p.z + p2.z) / 2);
                        ctx.stroke();
                        connections++;
                    }
                }
            });

            // Draw particles
            particles.forEach((p) => {
                const driftX = Math.sin(time * p.speed + p.phase) * (0.4 / p.z);
                const driftY = Math.cos(time * p.speed * 0.8 + p.phase) * (0.4 / p.z);

                p.x += p.vx + driftX;
                p.y += p.vy + driftY;

                if (blasted) {
                    p.vx *= 0.92; // faster damping: was 0.95
                    p.vy *= 0.92;
                    p.glow *= 0.94;
                }

                const flicker = Math.sin(time * p.pulseSpeed + p.phase) * 0.15 + 0.85;
                const padding = 100;
                if (p.x < -padding) p.x = width + padding;
                if (p.x > width + padding) p.x = -padding;
                if (p.y < -padding) p.y = height + padding;
                if (p.y > height + padding) p.y = -padding;

                const finalSize = p.size / p.z * (1 + p.glow * 1.5) * flicker;
                const opacity = (params.baseOpacity / p.z) * (1 + p.glow * 2.5) * flicker;

                // Core particle
                ctx.beginPath();
                ctx.arc(p.x, p.y, finalSize, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${p.color}, ${Math.min(1, opacity)})`;
                ctx.fill();

                // Outer glow halo
                ctx.beginPath();
                ctx.arc(p.x, p.y, finalSize * 2.8, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${p.color}, ${opacity * 0.45})`;
                ctx.fill();

                // Extra soft halo
                ctx.beginPath();
                ctx.arc(p.x, p.y, finalSize * 5, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${p.color}, ${opacity * 0.12})`;
                ctx.fill();
            });

            animationFrameId = requestAnimationFrame(draw);
        };

        const handleResize = () => {
            init();
            if (isVisible) {
                draw();
            }
        };

        window.addEventListener('resize', handleResize);
        init();

        // Delayed blast — gives particles time to settle before firing
        const blastTimer = setTimeout(triggerBlast, 800); // was 200ms

        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
            clearTimeout(blastTimer);
            observer.disconnect();
        };
    }, []);

    return (
        <div ref={containerRef} className="absolute inset-0 w-full h-full overflow-hidden" style={{ zIndex: 0 }}>
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ imageRendering: 'auto' }}
            />
        </div>
    );
};

export default NeuralNetworkBackground;